import { useState, useEffect } from 'react';
import { F, Btn } from '../../lib/bricks';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { List, Row, Empty, Section, FieldLabel, TextLink } from '../calm/Calm';
import { STATE_TEXT, ENTITY_TYPES, dialogFor, fmtDay, readable, cleanOwners, ownersError, todayIso } from './fintrac';

const TITLES = {
  officer: ['Compliance officer', 'The person responsible for your program. In a small office this can be the broker of record.'],
  policies: ['Policies and procedures', 'Record when a senior officer approved your written policies. Keep the document itself in your files.'],
  risk: ['Risk assessment', 'Describe your risk for each area. A new assessment is added to the record; earlier ones are kept.'],
  plan: ['Training plan', 'Record when your written, ongoing training plan for agents and staff was approved.'],
  training: ['Log training', 'Who was trained, when and on what. Each entry is kept for five years.'],
  review: ['Effectiveness review', 'A review of your policies, risk assessment and training, at least every two years. Report the findings to a senior officer within 30 days.'],
  ownership: ['Beneficial ownership', 'For a corporation, trust or other entity on a purchase or sale. Saved records can\u2019t be edited; add a new one if something changes.'],
};

function blank(kind, data) {
  const t = todayIso();
  const pr = data?.program || {};
  switch (kind) {
    case 'officer': return { officer_name: pr.officer_name || '', officer_appointed_on: pr.officer_appointed_on || t };
    case 'policies': return { policies_approved_on: t, policies_approved_by: pr.policies_approved_by || pr.officer_name || '', policies_version: '' };
    case 'plan': return { training_plan_approved_on: t };
    case 'risk': return { assessed_on: t, overall: 'medium', factors: {} };
    case 'training': return { person_name: '', user_id: '', trained_on: t, topic: 'Know your client and record keeping', minutes: '' };
    case 'review': return { reviewed_on: t, reviewer_name: '', reviewer_kind: 'internal', scope: { policies: true, risk: true, training: true }, findings: '', reported_to: pr.officer_name || '', reported_on: '' };
    case 'ownership': return { transaction_id: data?.deals?.[0]?.id || '', entity_name: '', entity_type: 'corporation', directors: '', owners: [{ name: '', address: '', percent: '' }], no_owner_over_threshold: false, structure: '', confirmed_how: '', could_not_confirm: false, senior_officer_verified: false, checked_on: t };
    default: return {};
  }
}

const ROUTE = {
  officer: ['PATCH', 'program'], policies: ['PATCH', 'program'], plan: ['PATCH', 'program'],
  risk: ['POST', 'risk-assessments'], training: ['POST', 'training'], review: ['POST', 'reviews'], ownership: ['POST', 'beneficial-ownership'],
};

export default function FintracProgram({ data, p, dark, open, setOpen, save }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(blank(open, data)); }, [open]); // eslint-disable-line

  const inputStyle = { width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box', marginTop: 8 };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const field = (k, label, props = {}) => (
    <div><FieldLabel>{label}</FieldLabel><input value={form[k] ?? ''} onChange={set(k)} style={inputStyle} {...props} /></div>
  );
  const check = (k, label) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: p.ink2 }}>
      <input type="checkbox" checked={!!form[k]} onChange={set(k)} style={{ accentColor: p.ink }} />{label}
    </label>
  );

  const els = data?.elements || [];
  const submit = async () => {
    const [method, path] = ROUTE[open];
    let body = { ...form };
    if (open === 'ownership') body = { ...body, owners: cleanOwners(form.owners) };
    if (open === 'training' && !body.user_id) delete body.user_id;
    setBusy(true);
    try { if (await save(method, path, body)) setOpen(null); } finally { setBusy(false); }
  };
  const invalid = (() => {
    if (open === 'officer') return !String(form.officer_name || '').trim();
    if (open === 'training') return !String(form.person_name || '').trim() || !String(form.topic || '').trim();
    if (open === 'review') return !String(form.reviewer_name || '').trim();
    if (open === 'risk') return !Object.values(form.factors || {}).some((v) => String(v || '').trim());
    if (open === 'ownership') return !form.transaction_id || !String(form.entity_name || '').trim() || !!ownersError(form.owners);
    return false;
  })();

  return (
    <>
      <Section title="Your compliance program" testid="fintrac-elements" note={data?.note}>
        <List>
          {els.map((e) => (
            <Row key={e.key} testid={`fintrac-el-${e.key}`} title={e.title}
              meta={readable(e.detail)} next={e.next ? readable(e.next) : null}
              status={STATE_TEXT[e.state]} attention={e.state === 'due' || e.state === 'missing'}
              aside={<TextLink testid={`fintrac-act-${e.key}`} onClick={() => setOpen(dialogFor(e))}>{e.action}</TextLink>} />
          ))}
        </List>
      </Section>

      <Section title="Beneficial ownership" testid="fintrac-ownership"
        note="Entity clients on purchases and sales: who owns or controls 25% or more, and how you confirmed it."
        action={(data?.deals || []).length ? <TextLink testid="fintrac-add-ownership" onClick={() => setOpen('ownership')}>Add record</TextLink> : null}>
        {(data?.ownership || []).length === 0 ? <Empty>No entity clients recorded yet.</Empty> : (
          <List>
            {data.ownership.map((o) => (
              <Row key={o.transaction_id} testid={`fintrac-bo-${o.transaction_id}`} title={o.entity_name}
                meta={[o.property_address, o.deal_type === 'sale' ? 'Sale' : 'Purchase', o.checked_on ? `Checked ${fmtDay(o.checked_on)}` : null]}
                next={o.complete ? (o.keep_until ? `Keep until ${fmtDay(o.keep_until)}` : null) : `Still needed: ${o.gaps.join(', ')}`}
                status={o.complete ? 'Complete' : 'Incomplete'} attention={!o.complete} />
            ))}
          </List>
        )}
      </Section>

      <Section title="Training log" testid="fintrac-training"
        action={<TextLink testid="fintrac-log-training" onClick={() => setOpen('training')}>Log training</TextLink>}>
        {(data?.training || []).length === 0 ? <Empty>No training logged yet.</Empty> : (
          <List>
            {data.training.slice(0, 6).map((t) => (
              <Row key={t.id} title={t.person_name} meta={[t.topic, t.minutes ? `${t.minutes} min` : null]} status={fmtDay(t.trained_on)} />
            ))}
          </List>
        )}
      </Section>

      <Section title="Reporting reminders" testid="fintrac-reminders">
        <List>
          {(data?.reminders || []).map((r) => (
            <Row key={r.key} title={r.title} meta={r.body} wide={false} />
          ))}
        </List>
      </Section>

      <Section title="Records" testid="fintrac-records" note="Kept at least five years from the day each is made. Saved records can't be edited or removed before then.">
        {(data?.records || []).length === 0 ? <Empty>Nothing recorded yet.</Empty> : (
          <List>
            {data.records.slice(0, 8).map((r, i) => (
              <Row key={`${r.kind}-${i}`} title={r.kind} meta={[r.label, fmtDay(r.made_on)]} status={`Keep until ${fmtDay(r.keep_until)}`} />
            ))}
          </List>
        )}
      </Section>

      <Dialog open={!!open} onOpenChange={(o) => { if (!o) setOpen(null); }}>
        <DialogContent data-testid="fintrac-dialog" style={{ maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>{TITLES[open]?.[0]}</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>{TITLES[open]?.[1]}</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'grid', gap: 14 }}>
            {open === 'officer' && (<>{field('officer_name', 'Name')}{field('officer_appointed_on', 'Appointed on', { type: 'date' })}</>)}
            {open === 'policies' && (<>{field('policies_approved_on', 'Approved on', { type: 'date' })}{field('policies_approved_by', 'Approved by (senior officer)')}{field('policies_version', 'Version or file name')}</>)}
            {open === 'plan' && field('training_plan_approved_on', 'Plan approved on', { type: 'date' })}
            {open === 'risk' && (<>
              {field('assessed_on', 'Assessed on', { type: 'date' })}
              {(data?.risk_factors || []).map((f) => (
                <div key={f.key}><FieldLabel>{f.label}</FieldLabel>
                  <textarea rows={2} value={form.factors?.[f.key] || ''} onChange={(e) => setForm((x) => ({ ...x, factors: { ...x.factors, [f.key]: e.target.value } }))} style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
              ))}
              <div><FieldLabel>Overall risk</FieldLabel>
                <select value={form.overall || 'medium'} onChange={set('overall')} style={{ ...inputStyle, background: p.bg }}>
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                </select></div>
            </>)}
            {open === 'training' && (<>
              <div><FieldLabel>Who</FieldLabel>
                <select value={form.user_id || ''} onChange={(e) => { const who = (data?.people || []).find((x) => x.id === e.target.value); setForm((f) => ({ ...f, user_id: e.target.value, person_name: who ? who.name : f.person_name })); }} style={{ ...inputStyle, background: p.bg }}>
                  <option value="">Someone else</option>
                  {(data?.people || []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select></div>
              {!form.user_id && field('person_name', 'Name')}
              {field('topic', 'Topic')}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{field('trained_on', 'Date', { type: 'date' })}{field('minutes', 'Minutes', { inputMode: 'numeric' })}</div>
            </>)}
            {open === 'review' && (<>
              {field('reviewed_on', 'Reviewed on', { type: 'date' })}
              {field('reviewer_name', 'Reviewer')}
              <div><FieldLabel>Reviewer is</FieldLabel>
                <select value={form.reviewer_kind} onChange={set('reviewer_kind')} style={{ ...inputStyle, background: p.bg }}>
                  <option value="internal">Someone in the office</option><option value="external">An outside reviewer</option>
                </select></div>
              <div><FieldLabel>Findings</FieldLabel><textarea rows={3} value={form.findings || ''} onChange={set('findings')} style={{ ...inputStyle, resize: 'vertical' }} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{field('reported_to', 'Reported to')}{field('reported_on', 'Reported on', { type: 'date' })}</div>
              <div style={{ fontSize: 12.5, color: p.ink3 }}>Covers your policies, risk assessment and training.</div>
            </>)}
            {open === 'ownership' && (<>
              <div><FieldLabel>Deal</FieldLabel>
                <select value={form.transaction_id} onChange={set('transaction_id')} style={{ ...inputStyle, background: p.bg }}>
                  {(data?.deals || []).map((d) => <option key={d.id} value={d.id}>{d.property_address} · {d.deal_type === 'sale' ? 'Sale' : 'Purchase'}</option>)}
                </select></div>
              <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 12 }}>
                {field('entity_name', 'Entity name')}
                <div><FieldLabel>Type</FieldLabel>
                  <select value={form.entity_type} onChange={set('entity_type')} style={{ ...inputStyle, background: p.bg }}>
                    {ENTITY_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select></div>
              </div>
              {field('directors', 'Directors or trustees')}
              <div><FieldLabel>Owners of 25% or more</FieldLabel>
                {(form.owners || []).map((o, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: 8 }}>
                    {['name', 'address', 'percent'].map((k) => (
                      <input key={k} aria-label={`Owner ${i + 1} ${k}`} placeholder={k === 'percent' ? '%' : k === 'name' ? 'Name' : 'Address'} value={o[k]}
                        inputMode={k === 'percent' ? 'decimal' : undefined}
                        onChange={(e) => setForm((f) => ({ ...f, owners: f.owners.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)) }))} style={inputStyle} />
                    ))}
                  </div>
                ))}
                <div style={{ marginTop: 8 }}><TextLink onClick={() => setForm((f) => ({ ...f, owners: [...f.owners, { name: '', address: '', percent: '' }] }))}>Add owner</TextLink></div>
                {ownersError(form.owners) ? <div style={{ fontSize: 12.5, color: p.ink2, marginTop: 6 }}>{ownersError(form.owners)}</div> : null}
              </div>
              {check('no_owner_over_threshold', 'No one owns or controls 25% or more')}
              {field('structure', 'Ownership and control structure')}
              {field('confirmed_how', 'How you confirmed it', { placeholder: 'e.g. Articles of incorporation and share register' })}
              {check('could_not_confirm', "Couldn't confirm the ownership")}
              {form.could_not_confirm && check('senior_officer_verified', "Verified the most senior managing officer's identity")}
            </>)}
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setOpen(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} data-testid="fintrac-save" disabled={busy || invalid} onClick={submit}>Save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
